import { createContext, useContext, type PropsWithChildren } from 'react'
import type { StorefrontConfig } from '@commerce/tokens'

/**
 * The authored storefront config, made available to the tree.
 *
 * It deliberately does **not** live in Redux. The store holds `IConfig` — the
 * narrow read view the ported promotion engine needs — and `IConfig` has no
 * room for `commerce.locale`, `commerce.placeholderImage` or the
 * storefront-wide `commerce.hidePrices`, all of which a page needs. Widening
 * `IConfig` to carry them would push UI concerns into the type that exists
 * precisely to keep them out.
 *
 * So there are two, with one owner each: this context for what the storefront
 * authored, the store for what the session derives. `usePresentationContext`
 * is where they meet.
 */
const StorefrontConfigContext = createContext<StorefrontConfig | null>(null)

export const StorefrontConfigProvider = ({
  config,
  children,
}: PropsWithChildren<{ config: StorefrontConfig }>) => (
  <StorefrontConfigContext.Provider value={config}>{children}</StorefrontConfigContext.Provider>
)

/**
 * Throws rather than returning a default. A missing provider means boot never
 * ran, and a silent fallback would render a storefront with someone else's
 * currency — the kind of defect that reaches a customer.
 */
export const useStorefrontConfig = (): StorefrontConfig => {
  const config = useContext(StorefrontConfigContext)
  if (!config) {
    throw new Error(
      'useStorefrontConfig must be used inside <StorefrontConfigProvider> — did boot dispatch the config?',
    )
  }
  return config
}
